﻿<template>
<section>
<!-- HEADER WITH CONTROLS -->
<div class="card bcx-card mb-3">
  <div class="card-body d-flex flex-column flex-lg-row justify-content-between gap-3">
    <div class="min-w-0">
      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span class="badge rounded-pill bg-success-subtle text-success-emphasis d-inline-flex align-items-center gap-1">
          <span class="bcx-live-dot"></span>
          Live area scan
        </span>
        <span class="badge rounded-pill bg-light text-secondary border">5km operating radius</span>
      </div>
      <h5 class="card-title fw-bold mb-1">Emergency Contacts &amp; Location</h5>
      <p class="text-secondary text-xs mb-0 text-truncate">
        {{ locationName }}
      </p>
    </div>

    <div class="row row-cols-1 row-cols-sm-3 g-2" style="min-width: 0; max-width: 44rem;">
      <div class="col">
        <div class="d-flex align-items-center gap-2 border rounded-3 p-2 h-100">
          <span class="bcx-icon-chip bcx-icon-chip-sm flex-shrink-0"><i class="bi bi-building"></i></span>
          <div class="min-w-0">
            <span class="d-block text-uppercase text-secondary text-xxs fw-bold">Municipality</span>
            <strong class="text-xs text-truncate d-block">{{ municipality }}</strong>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="d-flex align-items-center gap-2 border rounded-3 p-2 h-100">
          <span class="bcx-icon-chip bcx-icon-chip-sm flex-shrink-0"><i class="bi bi-droplet-fill"></i></span>
          <div class="min-w-0">
            <span class="d-block text-uppercase text-secondary text-xxs fw-bold">Water/Elect. Utility</span>
            <strong class="text-xs text-truncate d-block">{{ waterUtility }}</strong>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="d-flex align-items-center gap-2 border rounded-3 p-2 h-100">
          <span class="bcx-icon-chip bcx-icon-chip-sm flex-shrink-0"><i class="bi bi-receipt"></i></span>
          <div class="min-w-0">
            <span class="d-block text-uppercase text-secondary text-xxs fw-bold">Tariff Regime</span>
            <strong class="text-xs text-truncate d-block">{{ tariffRegime }}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- MAP SECTION -->
<div class="card bcx-card mb-3">
  <div class="card-body">
    <div class="d-flex align-items-start justify-content-between gap-3 mb-2">
      <div>
        <p class="text-uppercase text-secondary text-xxs fw-bold mb-1">Location Intelligence</p>
        <h6 class="fw-bold mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-map text-danger"></i>
          Service Radius Map
        </h6>
      </div>
      <span class="badge rounded-pill text-bg-danger">5km radius</span>
    </div>

    <div
      class="row g-3 align-items-stretch mb-4 location-feed-layout"
      :class="{ 'location-feed-layout-active': should_show_live_feed_panel }"
    >
      <div :class="should_show_live_feed_panel ? 'col-12 col-lg-8 d-flex' : 'col-12'">
        <div
          ref="map_wrapper"
          class="w-100 map-height overflow-hidden position-relative rounded-2 border"
        >

          <!-- GOOGLE MAP -->
          <div ref="mapContainer" class="w-100 h-100 rounded-2"></div>

          <!-- LOADING OVERLAY -->
          <div
            v-if="mapLoading"
            class="position-absolute top-0 start-0 w-100 h-100
                   d-flex align-items-center justify-content-center
                   map-loading-overlay text-center"
          >
            <div>
              <div class="spinner-border spinner-border-sm text-danger mb-3" role="status" aria-hidden="true"></div>
              <p class="fw-semibold mb-1">
                Loading Map &amp; Nearby Services
              </p>
              <p class="text-secondary text-xs mb-0">
                Searching within 5km radius...
              </p>
            </div>
          </div>

          <!-- MAP ERROR OVERLAY -->
          <div
            v-else-if="mapError"
            class="position-absolute top-0 start-0 w-100 h-100
                   d-flex align-items-center justify-content-center
                   map-error-overlay text-center"
          >
            <div>
              <p class="text-secondary text-xs mb-0">
                Unable to load map
              </p>
              <small class="text-secondary">
                {{ locationName }}
              </small>
            </div>
          </div>

          <!-- CLICK TO INTERACT OVERLAY -->
          <div
            v-else-if="show_map_overlay"
            class="position-absolute top-0 start-0 w-100 h-100
                   d-flex align-items-center justify-content-center
                   map-interact-overlay"
            @click.stop="activate_map"
          >
            <div class="text-center">
              <i class="bi bi-hand-index-thumb fs-2 mb-2 d-block"></i>
              <p class="fw-semibold mb-0">
                Click to interact with map
              </p>
            </div>
          </div>

          <div class="position-absolute bottom-0 start-0 m-3 badge rounded-pill bg-white text-dark border shadow-sm d-inline-flex align-items-center gap-1 fw-semibold">
            <i class="bi bi-geo-alt-fill text-danger"></i>
            <span>{{ locationName }}</span>
          </div>

        </div>
      </div>

      <div v-if="should_show_live_feed_panel" ref="live_feed_panel" class="col-12 col-lg-4 d-flex">
        <LivePropertyFeed
          :feed="liveFeed"
          :location-name="locationName"
          :coordinates="propertyLocation"
          :selected-location="active_live_feed_location"
        />
      </div>
    </div>
  </div>
</div>


<!-- CURRENT PROPERTY SHOPPING CENTRE (IF APPLICABLE) -->
<div v-if="propertyShoppingCenter" class="card bcx-card mb-4">
  <div class="card-body">
    <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
      <div>
        <h6 class="fw-bold mb-1">
          <i class="fas fa-store text-success me-2"></i>
          Current Property Shopping Centre
        </h6>
        <p class="text-secondary text-xs mb-0">Confirmed anchors and travel status for the selected property.</p>
      </div>
      <span class="badge rounded-pill bg-success-subtle text-success-emphasis">Your Property</span>
    </div>

    <!-- Property Shopping Centre Card -->
    <div class="card bcx-card h-100">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div class="d-flex align-items-start flex-grow-1">
            <span class="bcx-icon-chip bcx-icon-chip-success me-3">
              <i class="fas fa-store"></i>
            </span>
            <div class="flex-grow-1">
              <div class="fw-semibold text-sm">
                {{ getPropertyDisplayName(propertyShoppingCenter) }}
              </div>
              <div class="text-secondary text-xs">Primary operating location</div>
            </div>
          </div>
        </div>

        <!-- Property Anchor Tenants - WITH SHOW MORE/LESS -->
        <div v-if="propertyAnchorTenants && propertyAnchorTenants.length > 0" class="mb-3">
          <small class="text-uppercase text-secondary text-xxs fw-bold d-block mb-2">Confirmed Anchor Tenants</small>
          <div class="d-flex flex-wrap gap-2 mb-2">
            <span
              v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(propertyShoppingCenter)"
              :key="tenantIndex"
              class="badge text-xs"
              :class="getTenantCategoryClass(tenant.category)"
            >
              {{ tenant.name }}
            </span>
          </div>

          <!-- Show More/Less Button -->
          <button
            v-if="shouldShowMoreAnchorTenants(propertyShoppingCenter)"
            type="button"
            class="btn btn-sm btn-outline-success rounded-3 px-3 shadow-sm"
            @click="toggleAnchorTenants(propertyShoppingCenter)"
          >
            <span class="text-xs">+{{ propertyAnchorTenants.length - getVisibleAnchorTenants(propertyShoppingCenter).length }} more</span>
          </button>
          <button
            v-else-if="propertyAnchorTenants.length > 3"
            type="button"
            class="btn btn-sm btn-outline-success rounded-3 px-3 shadow-sm"
            @click="toggleAnchorTenants(propertyShoppingCenter)"
          >
            <span class="text-xs">Show less</span>
          </button>
        </div>

        <!-- ETA & DISTANCE BADGE -->
        <div class="row g-1 bg-light rounded-3 p-2">
          <div class="col-6">
            <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
              <i class="bi bi-clock-fill me-1"></i>
              ETA
            </small>
            <small class="fw-semibold">
              {{ propertyShoppingCenter.eta || 'N/A' }}
            </small>
          </div>
          <div class="col-6">
            <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
              <i class="bi bi-geo-alt-fill me-1"></i>
              Distance
            </small>
            <small class="fw-semibold">
              {{ formatDistance(propertyShoppingCenter.distance || 0) }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- COMPETITOR SHOPPING CENTRES SECTION -->
<div v-if="competitorShoppingCenters.length > 0" class="card bcx-card mb-4">
  <div class="card-body">
    <div class="d-flex align-items-start justify-content-between gap-3 mb-2">
      <div>
        <h6 class="fw-bold mb-1">
          <i class="fas fa-shopping-cart text-purple me-2"></i>
          Nearby Competitor Shopping Centres
        </h6>
        <p class="text-secondary text-xs mb-0">Anchor tenant overlap and route proximity within the local market.</p>
      </div>
      <span class="badge rounded-pill bg-light text-secondary border">{{ competitorShoppingCenters.length }} found</span>
    </div>
    <div class="mb-2">
      <p class="text-secondary text-xs">Data sourced from Google Places. Competitor anchor tenant details are indicative and should be validated manually.</p>
    </div>

    <!-- First 3 competitor centres -->
    <div class="row g-3">
      <div
        v-for="(center, index) in visibleCompetitorCenters"
        :key="center.place_id"
        class="col-12 col-md-6 col-lg-4"
      >
        <div
          class="card bcx-card h-100 border-2"
          :class="{
            'border-danger': center.competitor_score >= 80,
            'border-warning': center.competitor_score >= 60 && center.competitor_score < 80,
            'border-info': center.competitor_score >= 40 && center.competitor_score < 60,
            'border-secondary': center.competitor_score < 40
          }"
        >
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-start flex-grow-1">
                <span class="bcx-icon-chip me-3"
                   :class="{
                     'bcx-icon-chip-danger': center.competitor_score >= 80,
                     'bcx-icon-chip-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                     'bcx-icon-chip-info': center.competitor_score >= 40 && center.competitor_score < 60,
                     'bcx-icon-chip-muted': center.competitor_score < 40
                   }">
                  <i class="fas fa-shopping-cart"></i>
                </span>
                <div class="flex-grow-1">
                  <div class="fw-semibold text-xs">
                    {{ center.name }}
                  </div>
                  <div class="text-secondary text-xs">
                    {{ center.vicinity || center.formatted_address || 'Address not available' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Competitor Anchor Tenants - WITH SHOW MORE/LESS -->
            <div v-if="center.anchor_tenants && center.anchor_tenants.length > 0" class="mb-3">
              <small class="text-uppercase text-secondary text-xxs fw-bold d-block mb-2">Anchor Tenants</small>
              <div class="d-flex flex-wrap gap-2 mb-2">
                <span
                  v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(center)"
                  :key="tenantIndex"
                  class="badge text-xs"
                  :class="getTenantCategoryClass(tenant.category)"
                  :title="getTenantTooltip(tenant)"
                >
                  {{ tenant.name }}{{ tenant.assumed ? '*' : '' }}
                </span>
              </div>

              <!-- Show More/Less Button -->
              <button
                v-if="shouldShowMoreAnchorTenants(center)"
                @click="toggleAnchorTenants(center)"
                class="btn btn-sm rounded-3 px-3 shadow-sm"
                :class="{
                  'btn-outline-danger': center.competitor_score >= 80,
                  'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                  'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                  'btn-outline-secondary': center.competitor_score < 40
                }"
              >
                <span class="text-xs">view more</span>
              </button>
              <button
                v-else-if="center.anchor_tenants.length > 3"
                @click="toggleAnchorTenants(center)"
                class="btn btn-sm rounded-3 px-3 shadow-sm"
                :class="{
                  'btn-outline-danger': center.competitor_score >= 80,
                  'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                  'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                  'btn-outline-secondary': center.competitor_score < 40
                }"
              >
                <span class="text-xs">Show less</span>
              </button>
            </div>

            <!-- ETA & DISTANCE BADGE -->
            <div class="row g-1 bg-light rounded-3 p-2 mb-3">
              <div class="col-6">
                <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
                  <i class="bi bi-clock-fill me-1"></i>
                  ETA
                </small>
                <small class="fw-semibold">
                  {{ center.eta || 'N/A' }}
                </small>
              </div>
              <div class="col-6">
                <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
                  <i class="bi bi-geo-alt-fill me-1"></i>
                  Distance
                </small>
                <small class="fw-semibold">
                  {{ formatDistance(center.distance) }}
                </small>
              </div>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-content-end align-items-center pt-2 border-top">
              <div class="d-flex gap-2">
                <button
                  @click="toggleDirections(center)"
                  class="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  :class="showRoutes[center.name] ? 'btn-info' : 'btn-outline-info'"
                  style="width: 32px; height: 32px;"
                  :title="showRoutes[center.name] ? 'Hide Route' : 'Show Route'"
                >
                  <i class="bi bi-geo-alt"></i>
                </button>
                <a
                  v-if="center.website"
                  :href="center.website"
                  target="_blank"
                  class="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 32px; height: 32px;"
                  title="Open Website"
                >
                  <i class="bi bi-globe2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show All Competitor Centres Button -->
    <div v-if="competitorShoppingCenters.length > 3" class="text-center mt-3">
      <button
        @click="showAllCompetitorCenters = !showAllCompetitorCenters"
        class="btn btn-sm btn-outline-danger rounded-3 d-inline-flex align-items-center gap-1 px-3 shadow-sm"
      >
        <i class="bi text-xs" :class="showAllCompetitorCenters ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        <span class="text-xs">{{ showAllCompetitorCenters ? 'Show Less' : `Show All ${competitorShoppingCenters.length} Competitor Centres` }}</span>
      </button>
    </div>

    <!-- ALL COMPETITOR CENTRES (EXPANDED VIEW) -->
    <div v-if="showAllCompetitorCenters && competitorShoppingCenters.length > 3" class="mt-4 pt-4 border-top">
      <h6 class="fw-bold mb-3">All Nearby Competitor Centres</h6>
      <div class="row g-3">
        <div
          v-for="(center, index) in remainingCompetitorCenters"
          :key="'all-' + center.place_id"
          class="col-12 col-md-6 col-lg-4"
        >
          <div
            class="card bcx-card h-100 border-2"
            :class="{
              'border-danger': center.competitor_score >= 80,
              'border-warning': center.competitor_score >= 60 && center.competitor_score < 80,
              'border-info': center.competitor_score >= 40 && center.competitor_score < 60,
              'border-secondary': center.competitor_score < 40
            }"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="d-flex align-items-start flex-grow-1">
                  <span class="bcx-icon-chip me-3"
                     :class="{
                       'bcx-icon-chip-danger': center.competitor_score >= 80,
                       'bcx-icon-chip-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                       'bcx-icon-chip-info': center.competitor_score >= 40 && center.competitor_score < 60,
                       'bcx-icon-chip-muted': center.competitor_score < 40
                     }">
                    <i class="fas fa-shopping-cart"></i>
                  </span>
                  <div class="flex-grow-1">
                    <div class="fw-semibold text-xs">
                      {{ center.name }}
                    </div>
                    <div class="text-secondary text-xs">
                      {{ center.vicinity || 'Address not available' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Anchor Tenants WITH SHOW MORE/LESS -->
              <div v-if="center.anchor_tenants && center.anchor_tenants.length > 0" class="mb-2">
                <small class="text-uppercase text-secondary text-xxs fw-bold d-block mb-2">Anchor Tenants</small>
                <div class="d-flex flex-wrap gap-2 mb-2">
                  <span
                    v-for="(tenant, tenantIndex) in getVisibleAnchorTenants(center)"
                    :key="tenantIndex"
                    class="badge text-xs"
                    :class="getTenantCategoryClass(tenant.category)"
                    :title="getTenantTooltip(tenant)"
                  >
                    {{ tenant.name }}{{ tenant.assumed ? '*' : '' }}
                  </span>
                </div>

                <!-- Show More/Less Button -->
                <button
                  v-if="shouldShowMoreAnchorTenants(center)"
                  @click="toggleAnchorTenants(center)"
                  class="btn btn-sm rounded-3 px-3 shadow-sm"
                  :class="{
                    'btn-outline-danger': center.competitor_score >= 80,
                    'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                    'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                    'btn-outline-secondary': center.competitor_score < 40
                  }"
                >
                  <span class="text-xs">view more</span>
                </button>
                <button
                  v-else-if="center.anchor_tenants.length > 3"
                  @click="toggleAnchorTenants(center)"
                  class="btn btn-sm rounded-3 px-3 shadow-sm"
                  :class="{
                    'btn-outline-danger': center.competitor_score >= 80,
                    'btn-outline-warning': center.competitor_score >= 60 && center.competitor_score < 80,
                    'btn-outline-info': center.competitor_score >= 40 && center.competitor_score < 60,
                    'btn-outline-secondary': center.competitor_score < 40
                  }"
                >
                  <span class="text-xs">Show less</span>
                </button>
              </div>

              <!-- ETA & Distance -->
              <div class="d-flex justify-content-between align-items-center mb-2 bg-light rounded-3 p-2">
                <small>
                  <i class="bi bi-clock me-1"></i>{{ center.eta || 'N/A' }}
                </small>
                <small>
                  <i class="bi bi-geo-alt me-1"></i>{{ formatDistance(center.distance) }}
                </small>
              </div>

              <!-- Actions -->
              <div class="d-flex justify-content-end align-items-center pt-2 border-top">
                <div class="d-flex gap-1">
                  <button
                    @click="toggleDirections(center)"
                    class="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                    :class="showRoutes[center.name] ? 'btn-info' : 'btn-outline-info'"
                    style="width: 32px; height: 32px;"
                    :title="showRoutes[center.name] ? 'Hide Route' : 'Show Route'"
                  >
                    <i class="bi bi-geo-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- EMERGENCY CONTACTS -->
<div class="card bcx-card">
  <div class="card-body">
    <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
      <div>
        <h6 class="fw-bold mb-1">
          <i class="bi bi-phone-vibrate-fill text-danger me-2"></i>
          Emergency Services
          <span class="text-secondary text-xxs fw-normal">(Voice Dial coming 2027)</span>
          <span v-if="hasServicesOutsideRadius" class="ms-2">
            <i class="bi bi-exclamation-triangle-fill text-warning"
               title="Some services found outside 5km radius"></i>
          </span>
        </h6>
        <p class="text-secondary text-xs mb-0">Nearest police, medical, and fire response options.</p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <span class="badge rounded-pill bg-danger-subtle text-danger-emphasis">
          {{ nearestEmergencyServices.length }} found
        </span>
        <span v-if="hasServicesOutsideRadius" class="badge rounded-pill bg-warning-subtle text-warning-emphasis">
          Expanded search
        </span>
      </div>
    </div>

    <!-- IF CONTACTS FOUND -->
    <div v-if="nearestEmergencyServices.length > 0" class="row g-3">
      <div
        v-for="(place, index) in nearestEmergencyServices"
        :key="index"
        class="col-12 col-md-6 col-lg-4"
      >
        <div
          class="card bcx-card h-100 position-relative"
          :style="{ borderLeft: `4px solid ${routeColors[index % routeColors.length]}` }"
        >
          <div class="card-body">
            <!-- Outside Radius Indicator -->
            <div v-if="place.outsideStandardRadius"
                 class="position-absolute top-0 end-0 m-2"
                 data-bs-toggle="tooltip"
                 :title="`Found outside 5km radius (${Math.round(place.searchRadius/1000)}km)`">
              <span class="badge bg-warning text-dark text-xs">
                <i class="bi bi-signpost-fill me-1"></i>
                {{ Math.round(place.searchRadius/1000) }}km
              </span>
            </div>

            <!-- Service Name with Distance Indicator -->
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-start flex-grow-1">
                <i
                  :class="place.icon"
                  class="fs-5 me-2"
                  :style="{ color: routeColors[index % routeColors.length] }"
                ></i>

                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2">
                    <div class="fw-semibold text-xs">
                      {{ place.name }}
                    </div>
                    <span v-if="place.searchRadius > 5000" class="text-warning text-xxs">
                      (outside 5km)
                    </span>
                  </div>

                  <div class="text-secondary text-xs">
                    {{ place.vicinity || place.formatted_address }}
                  </div>

                  <div
                    v-if="place.formatted_phone_number"
                    class="text-secondary text-xs"
                  >
                    {{ format_phone_number(place.formatted_phone_number) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ETA & DISTANCE BADGE with Radius Info -->
            <div class="row g-1 bg-light rounded-3 p-2 mb-3">
              <div class="col-6">
                <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
                  <i class="bi bi-clock-fill me-1"></i>
                  ETA
                </small>
                <small class="fw-semibold">
                  {{ place.eta || 'N/A' }}
                </small>
              </div>
              <div class="col-6">
                <small class="text-uppercase text-secondary text-xxs fw-bold d-block">
                  <i class="bi bi-geo-alt-fill me-1"></i>
                  Distance
                </small>
                <small class="fw-semibold">
                  {{ formatDistance(place.distance) }}
                  <span v-if="place.searchRadius > 5000" class="text-warning text-xxs ms-1">
                    ({{ Math.round(place.searchRadius/1000) }}km search)
                  </span>
                </small>
              </div>
            </div>

            <!-- ACTION TOOLBAR -->
            <div class="pt-2 border-top d-flex justify-content-between align-items-center gap-2">
              <!-- Main Route Toggle -->
              <button
                @click="toggleDirections(place)"
                :class="showRoutes[place.name] ? 'btn-primary' : 'btn-outline-primary'"
                class="btn btn-sm rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 shadow-sm flex-grow-1"
              >
                <i class="text-xs" :class="showRoutes[place.name] ? 'bi bi-eye-fill' : 'bi bi-geo-alt-fill'"></i>
                <span class="text-xs">{{ showRoutes[place.name] ? 'Hide route' : 'Show route' }}</span>
              </button>

              <!-- Icon actions -->
              <div class="d-flex align-items-center gap-2">
                <!-- Call -->
                <button
                  v-if="place.formatted_phone_number"
                  @click="callPlace(place)"
                  class="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                  type="button"
                  data-bs-toggle="tooltip"
                  title="Call"
                  style="width: 32px; height: 32px;"
                >
                  <i class="bi bi-telephone-fill"></i>
                </button>

                <!-- Website -->
                <a
                  v-if="place.website"
                  :href="place.website"
                  target="_blank"
                  class="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                  data-bs-toggle="tooltip"
                  title="Open website"
                  style="width: 32px; height: 32px;"
                >
                  <i class="bi bi-globe2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- IF NO CONTACTS -->
    <div v-else class="bcx-dashed-empty text-secondary text-xs text-center py-4">
      <i class="bi bi-search fs-4 d-block mb-2"></i>
      Click the map to search for nearby emergency services within a 5km radius. If no services are found, the search automatically expands up to 200km.
    </div>

    <!-- Search Radius Info -->
    <div v-if="nearestEmergencyServices.length > 0" class="mt-3 pt-3 border-top">
      <small class="text-secondary text-xs d-flex align-items-center">
        <i class="bi bi-info-circle me-2"></i>
        <span v-if="hasServicesOutsideRadius">
          <span class="text-warning">Some services were found outside the 5km radius.</span> Expanded search performed up to 200km.
        </span>
        <span v-else>
          All services found within 5km radius.
        </span>
      </small>
    </div>
  </div>
</div>
</section>
</template>
